import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import { countInvoices } from "../../../graphql/reloadyard/custom-queries";
import {
  getInboundShipmentForInvoiceValidation,
  getOutboundShipmentForInvoiceValidation,
  invoiceServiceByInboundForValidation,
  invoiceServiceByOutboundForValidation,
} from "../../../graphql/invoice/invoice-queries";
import { updateReloadYard } from "../../../graphql/mutations";

import {
  Backdrop,
  CircularProgress,
  DialogContentText,
  makeStyles,
} from "@material-ui/core";

import { Confirm, SaveButton, useNotify } from "react-admin";
import { useForm } from "react-final-form";

import { SaveIcon, UploadIcon } from "../../../components/common/iconography";

import { logError } from "../../../utils/appUtils";
import {
  ExternalStatus,
  FilePaths,
  InvoiceStatus,
} from "../../../utils/constants";
import { saveRemoveFiles } from "../../../utils/fileUtils";
import {
  transformZapierInvoiceData,
  triggerEvent,
} from "../../../utils/zapierUtils";
import { flatRedirect } from "../../../utils/utils";
import {
  createLambdaInvoiceServices,
  createTransform,
  fetchReloadYard,
  logInvoiceCreateHistory,
  updateLambdaInbound,
  updateLambdaOutbound,
  setInvoiceSyncFailed,
} from "./InvoiceUtils";
import { sanitizeZapierData } from "../../../utils/zapierUtils";
import { syncableInvoiceCreateProps } from "../../../graphql/invoice/invoiceCRUD";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

const uniq = (arr) => [...new Set(arr.filter(Boolean))];
const hasVal = (v) => v !== undefined && v !== null && v !== "";

// Find the single shipment backing this invoice
function getSingleShipmentIds(formData) {
  const items = formData?.lineItems?.items || [];
  const inboundIds = uniq(items.map((i) => i?.inboundShipmentId));
  const outboundIds = uniq(items.map((i) => i?.outboundShipmentId));

  // should have *either* one inbound or one outbound
  const inboundId = inboundIds[0] || formData?.inboundShipmentId || null;
  const outboundId = outboundIds[0] || formData?.outboundShipmentId || null;

  return { inboundId, outboundId };
}

async function checkAlreadyInvoiced({ inboundId, outboundId }) {
  const tasks = [];

  if (inboundId) {
    tasks.push(
      API.graphql(
        graphqlOperation(invoiceServiceByInboundForValidation, {
          inboundShipmentId: inboundId,
          limit: 1,
        }),
      ).then((res) => {
        // If your custom query aliases the field, adjust the path below:
        const hit = res?.data?.invoiceServiceByInbound?.items?.[0];
        return hit
          ? {
              type: "inbound",
              shipmentId: inboundId,
              invoiceServiceId: hit.id,
              invoiceId: hit.invoiceId,
            }
          : null;
      }),
    );
  }

  if (outboundId) {
    tasks.push(
      API.graphql(
        graphqlOperation(invoiceServiceByOutboundForValidation, {
          outboundShipmentId: outboundId,
          limit: 1,
        }),
      ).then((res) => {
        const hit = res?.data?.invoiceServiceByOutbound?.items?.[0];
        return hit
          ? {
              type: "outbound",
              shipmentId: outboundId,
              invoiceServiceId: hit.id,
              invoiceId: hit.invoiceId,
            }
          : null;
      }),
    );
  }

  if (tasks.length === 0) return [];

  const results = await Promise.allSettled(tasks);
  // ignore rejected tasks so a transient error doesn’t block the user
  return results
    .filter((s) => s.status === "fulfilled" && s.value)
    .map((s) => s.value);
}

const checkInvoiceIdLinked = async ({ inboundId, outboundId }) => {
  const tasks = [];

  if (inboundId) {
    tasks.push(
      API.graphql(
        graphqlOperation(getInboundShipmentForInvoiceValidation, {
          id: inboundId,
        }),
      ).then((res) => {
        const inbound = res?.data?.getInboundShipment;
        const invoiceId = (inbound?.invoiceId || "").trim();
        return invoiceId
          ? {
              type: "inbound",
              shipmentId: inboundId,
              invoiceId,
              source: "invoiceIdShipment",
            }
          : null;
      }),
    );
  }

  if (outboundId) {
    tasks.push(
      API.graphql(
        graphqlOperation(getOutboundShipmentForInvoiceValidation, {
          id: outboundId,
        }),
      ).then((res) => {
        const outbound = res?.data?.getOutboundShipment;
        const invoiceId = (outbound?.invoiceId || "").trim();
        return invoiceId
          ? {
              type: "outbound",
              shipmentId: outboundId,
              invoiceId,
              source: "invoiceIdShipment",
            }
          : null;
      }),
    );
  }

  if (tasks.length === 0) return [];

  const results = await Promise.allSettled(tasks);
  return results
    .filter((s) => s.status === "fulfilled" && s.value)
    .map((s) => s.value);
};

const validateInvoiceUnicity = async (formData) => {
  debugger;
  try {
    // uside with invoice create form for a single shipment, or no shipment ata l
    const { inboundId, outboundId } = getSingleShipmentIds(formData);

    // Hard guard: if shipment already has any InvoiceService or invoiceId link, block
    const [serviceHits, invoiceIdHits] = await Promise.all([
      checkAlreadyInvoiced({ inboundId, outboundId }),
      checkInvoiceIdLinked({ inboundId, outboundId }),
    ]);
    const combinedHits = [...serviceHits, ...invoiceIdHits].filter(Boolean);
    if (combinedHits.length > 0) {
      return { reason: "ALREADY_INVOICED", alreadyInvoiced: combinedHits };
    }

    // Soft warning: possible duplicate *manual* invoice (same account + PO)
    // Only do this when no shipment IDs are present
    const isManuallyComposed =
      !inboundId &&
      !outboundId &&
      !formData.inboundShipmentId &&
      !formData.outboundShipmentId;
    if (isManuallyComposed && hasVal(formData.poNumber)) {
      const resp = await API.graphql(
        graphqlOperation(countInvoices, {
          filter: {
            reloadYardId: { eq: window.appUser.reloadYardId },
            accountId: { eq: formData.accountId },
            poNumber: { eq: formData.poNumber },
            status: { ne: InvoiceStatus.VOID },
          },
          limit: 1,
        }),
      );
      const duplicateInvoices = resp?.data?.searchInvoices?.total ?? 0;

      if (duplicateInvoices > 0) {
        // Warn: allow user to confirm and proceed
        return {
          reason: "DUPLICATE_PO",
          duplicateInvoices,
        };
      }
    }

    return null; // All clear
  } catch (err) {
    // Don’t block on validator failure; log and allow user to proceed
    logError("invoices:validateInvoiceUnicity", err, false);
    return null;
  }
};

export const ValidateCreateButton = ({
  backPath,
  account,
  initialValues,
  isSyncEnabled,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const history = useHistory();
  const notify = useNotify();
  const capturedRef = useRef({});
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const handleValidateSave = async () => {
    let messages = [];
    const formState = form.getState();
    const formData = formState.values;
    const isValid = !formState.invalid; // Form-level validation status. Submit to force field-level form validation.
    setIsSaving(true);
    debugger;

    if (isValid) {
      const isDuplicate = await validateInvoiceUnicity(formData);

      if (isDuplicate?.reason === "ALREADY_INVOICED") {
        notify("One or more shipments are already invoiced.", "error");
        setIsSaving(false);
        setIsDisabled(true);
        return; // Halt submission if duplicate found
      }

      if (isDuplicate?.reason === "DUPLICATE_PO") {
        // handle duplicate manual invoice
        messages.push(
          <>
            An invoice with this PO Number already exists for this customer.{" "}
            <br />
            Continue saving a potentially duplicate invoice?
          </>,
        );
      }
    }

    if (messages.length > 0) {
      // Open Confirm dialog.
      setMessage(
        <DialogContentText>
          {messages.map((msg, index) => (
            <React.Fragment key={index}>{msg}</React.Fragment>
          ))}
        </DialogContentText>,
      );
      setOpen(true);
    } else {
      if (!isValid) setIsSaving(false);
      handleSave(); // submit and force field-level form validation whether valid or not.
    }
  };

  const handleDialogClose = () => {
    setOpen(false);
    setIsSaving(false);
  };

  const handleDialogConfirm = async () => {
    setOpen(false);
    debugger;
    handleSave(); // proceed to create duplicate invoice
  };

  const handleSave = () => {
    props.handleSubmitWithRedirect(); // submit and force field-level form validation whether valid or not.
  };

  const transform = async (data) => {
    setIsSaving(true);
    debugger;
    capturedRef.current.lineItems = data.lineItems;
    let sanitized = null;

    try {
      const reloadYard = await fetchReloadYard();
      const prefix = reloadYard.invoiceNumberPrefix;
      const counter = ++reloadYard.invoiceCounter;

      await Promise.all([
        API.graphql(
          graphqlOperation(updateReloadYard, {
            input: {
              id: window.appUser?.reloadYardId,
              active: reloadYard.active,
              name: reloadYard.name,
              invoiceCounter: counter,
            },
          }),
        ),
        saveRemoveFiles(
          undefined,
          data,
          "attachments",
          `${FilePaths.INVOICE}/${Date.now()}`,
        ),
      ]);

      sanitized = createTransform(data, prefix, counter, isSyncEnabled);
    } catch (error) {
      logError("invoices:ValidateCreateButton:transform", error, false);
      return null;
    }

    if (isSyncEnabled) {
      sanitized.externalStatus = ExternalStatus.SYNC_PENDING;
    }

    return sanitized;
  };

  const onSuccess = async ({ data }) => {
    debugger;
    try {
      logInvoiceCreateHistory(data);

      const invoiceServiceCreateMap = {};
      let inboundUpdateMap = null;
      let outboundUpdateMap = null;

      const invoiceData = { ...data, lineItems: capturedRef.current.lineItems };
      const invoiceServiceInputs = createLambdaInvoiceServices(invoiceData);
      invoiceServiceCreateMap[invoiceData.id] = invoiceServiceInputs;

      if (initialValues.inboundShipment) {
        const inboundUpdateInput = updateLambdaInbound(
          initialValues.inboundShipment,
          invoiceData,
        );
        inboundUpdateMap = { [invoiceData.id]: inboundUpdateInput };
      } else if (initialValues.outboundShipment) {
        const outboundUpdateInput = updateLambdaOutbound(
          initialValues.outboundShipment,
          invoiceData,
        );
        outboundUpdateMap = { [invoiceData.id]: outboundUpdateInput };
      }

      const response = await API.post("transREST", "/invoiceCreate", {
        body: {
          invoice: data,
          invoiceServiceCreateMap: invoiceServiceCreateMap,
          inboundUpdateMap: inboundUpdateMap,
          outboundUpdateMap: outboundUpdateMap,
          user: window.appUser.email,
          reloadYardId: window.appUser.reloadYardId,
        },
      });
      if (response.error) throw response.error;

      if (isSyncEnabled) {
        try {
          const readyData = transformZapierInvoiceData(
            {
              ...data,
              lineItems: capturedRef.current.lineItems,
            },
            account?.services,
          );
          const validatedData = sanitizeZapierData(
            syncableInvoiceCreateProps,
            readyData,
          );

          triggerEvent(validatedData, "invoiceCreated").catch((error) => {
            logError("invoices:ValidateCreateButton:triggerEvent", error);
            setInvoiceSyncFailed(data);
          });
        } catch (error) {
          logError(
            "invoices:ValidateCreateButton:sanitizeZapierData",
            error,
            notify,
          );
          setInvoiceSyncFailed(data);
        }
      }

      notify("Invoice created successfully", "success");
      flatRedirect(history, `/invoices/${data.id}`, backPath, {});
    } catch (error) {
      logError("invoices:ValidateCreateButton:onSuccess", error, notify);
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("invoices:ValidateCreateButton:onFailure", error, notify);
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        {...props}
        icon={isSyncEnabled ? <UploadIcon /> : <SaveIcon />}
        submitOnEnter={false}
        undoable={false}
        saving={isSaving}
        disabled={isDisabled}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <Confirm
        isOpen={open}
        title="Save Confirmation"
        content={message}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};
